import { StatusLED } from "@/components/infra";

/**
 * Schematic of the digital-infrastructure ecosystem.
 * Hand-drawn flat-line SVG, chrome-on-white aesthetic.
 * Nodes are labeled; chrome accents mark "active" surfaces.
 */
function EcosystemDiagram({ className = "" }) {
  return (
    <svg
      viewBox="0 0 1380 560"
      className={className}
      fill="none"
      role="img"
      aria-label="Digital infrastructure ecosystem"
    >
      <defs>
        <linearGradient id="eco-chrome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2A2D34" />
          <stop offset="100%" stopColor="#9CA0A6" />
        </linearGradient>
        <linearGradient id="eco-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0F1117" />
          <stop offset="50%" stopColor="#888B92" />
          <stop offset="100%" stopColor="#0F1117" />
        </linearGradient>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#0F1117" />
        </marker>
      </defs>

      {/* Background grid */}
      <g opacity="0.18">
        {Array.from({ length: 18 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 80} x2={i * 80} y1="0" y2="560" stroke="#0F1117" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={`h${i}`} x1="0" x2="1380" y1={i * 80} y2={i * 80} stroke="#0F1117" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
      </g>

      {/* Layer labels (left margin) */}
      <g fontFamily="Space Mono, monospace" fontSize="9" fill="#71747B" letterSpacing="1.5">
        <text x="20" y="80">LAYER 01 · POWER</text>
        <text x="20" y="220">LAYER 02 · FACILITY</text>
        <text x="20" y="360">LAYER 03 · NETWORK</text>
        <text x="20" y="500">LAYER 04 · DEMAND</text>
      </g>

      {/* === LAYER 01 · POWER === */}
      <Node x="160" y="100" w="120" h="50" label="GRID" sub="Utility" icon="grid" />
      <line x1="280" y1="125" x2="320" y2="125" stroke="url(#eco-line)" strokeWidth="1.5" markerEnd="url(#arrow)" />
      <Node x="320" y="100" w="140" h="50" label="SUBSTATION" sub="MV / HV step-down" icon="substation" />
      <line x1="460" y1="125" x2="500" y2="125" stroke="url(#eco-line)" strokeWidth="1.5" markerEnd="url(#arrow)" />
      <Node x="500" y="100" w="140" h="50" label="SWITCHGEAR" sub="Distribution" filled icon="switchgear" />
      <line x1="640" y1="125" x2="680" y2="125" stroke="url(#eco-line)" strokeWidth="1.5" markerEnd="url(#arrow)" />
      <Node x="680" y="100" w="140" h="50" label="UPS" sub="Battery + flywheel" icon="ups" />
      <Node x="860" y="100" w="150" h="50" label="GENERATORS" sub="Diesel / fuel cell" icon="generators" />
      <path d="M 640 125 Q 750 55, 860 125" stroke="#0F1117" strokeWidth="1" strokeDasharray="3 3" fill="none" />
      <text x="750" y="50" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="9" fill="#71747B">FAILOVER</text>

      {/* === LAYER 02 · FACILITY === */}
      <rect x="160" y="220" width="900" height="120" stroke="#0F1117" strokeWidth="1.5" fill="#FAFAFB" vectorEffect="non-scaling-stroke" />
      <text x="170" y="240" fontFamily="Space Mono, monospace" fontSize="9" fill="#71747B" letterSpacing="1.5">
        DATA CENTER FACILITY · 24/7
      </text>

      <Node x="180" y="260" w="150" h="60" label="COOLING" sub="CRAC · chillers" icon="cooling" />
      <Node x="345" y="260" w="170" h="60" label="COMPUTE RACKS" sub="CPU · GPU · accelerators" filled icon="compute" />
      <Node x="530" y="260" w="130" h="60" label="PDU" sub="Rack-level power" icon="pdu" />
      <Node x="675" y="260" w="170" h="60" label="NETWORK SPINE" sub="Top-of-rack · core" icon="network" />
      <Node x="860" y="260" w="180" h="60" label="OPS FLOOR" sub="NOC · monitoring · BMS" icon="ops" />

      {/* Conditioned power from UPS into facility */}
      <line x1="750" y1="150" x2="750" y2="220" stroke="url(#eco-line)" strokeWidth="1.5" markerEnd="url(#arrow)" />
      <text x="760" y="190" fontFamily="Space Mono, monospace" fontSize="9" fill="#71747B">CONDITIONED POWER</text>

      {/* Internal flow arrows */}
      <line x1="330" y1="290" x2="345" y2="290" stroke="#0F1117" strokeWidth="1" markerEnd="url(#arrow)" />
      <line x1="515" y1="290" x2="530" y2="290" stroke="#0F1117" strokeWidth="1" markerEnd="url(#arrow)" />
      <line x1="660" y1="290" x2="675" y2="290" stroke="#0F1117" strokeWidth="1" markerEnd="url(#arrow)" />

      {/* === LAYER 03 · NETWORK === */}
      <Node x="160" y="360" w="170" h="50" label="METRO FIBER" sub="Carrier hotels · MMR" icon="metroFiber" />
      <Node x="345" y="360" w="180" h="50" label="LONG-HAUL FIBER" sub="Cross-continental" icon="longHaul" />
      <Node x="540" y="360" w="170" h="50" label="SUBSEA CABLES" sub="Trans-ocean" icon="subsea" />
      <Node x="725" y="360" w="160" h="50" label="EDGE NODES" sub="Last-mile presence" icon="edge" />
      <Node x="900" y="360" w="160" h="50" label="PEERING / IX" sub="Internet exchange" icon="peering" />

      {/* Network spine → metro fiber */}
      <line x1="760" y1="320" x2="760" y2="360" stroke="url(#eco-line)" strokeWidth="1.5" markerEnd="url(#arrow)" />

      {/* Network row tick connectors */}
      <line x1="330" y1="385" x2="345" y2="385" stroke="#0F1117" strokeWidth="1" />
      <line x1="525" y1="385" x2="540" y2="385" stroke="#0F1117" strokeWidth="1" />
      <line x1="710" y1="385" x2="725" y2="385" stroke="#0F1117" strokeWidth="1" />
      <line x1="885" y1="385" x2="900" y2="385" stroke="#0F1117" strokeWidth="1" />

      {/* === LAYER 04 · DEMAND === */}
      <Node x="160" y="460" w="150" h="50" label="AI TRAINING" sub="Hyperscale clusters" filled icon="aiTrain" />
      <Node x="325" y="460" w="150" h="50" label="AI INFERENCE" sub="Real-time serving" icon="aiInfer" />
      <Node x="490" y="460" w="130" h="50" label="CLOUD" sub="SaaS · PaaS · IaaS" icon="cloud" />
      <Node x="635" y="460" w="170" h="50" label="CONTENT / CDN" sub="Streaming · gaming" icon="cdn" />
      <Node x="820" y="460" w="160" h="50" label="ENTERPRISE" sub="Banks · health · gov" icon="enterprise" />

      {/* IX → demand band */}
      <line x1="980" y1="410" x2="980" y2="460" stroke="url(#eco-line)" strokeWidth="1.5" />
      <line x1="980" y1="460" x2="980" y2="460" stroke="url(#eco-line)" strokeWidth="1.5" markerEnd="url(#arrow)" />

      {/* === PEOPLE BAND (far right column) === */}
      <rect x="1100" y="220" width="260" height="240" stroke="#0F1117" strokeWidth="1.5" fill="#FAFAFB" vectorEffect="non-scaling-stroke" />
      <image
        href="/dcyb-logo.png"
        x="1300"
        y="228"
        width="48"
        height="16"
        preserveAspectRatio="xMaxYMin meet"
      />
      <text x="1110" y="240" fontFamily="Space Mono, monospace" fontSize="9" fill="#71747B" letterSpacing="1.5">
        THE PEOPLE · DCYB
      </text>
      {[
        { y: 260, label: "OPERATORS", sub: "Critical facilities, NOC" },
        { y: 320, label: "ENGINEERS", sub: "MEP, electrical, network" },
        { y: 380, label: "BUILDERS", sub: "Construction, commissioning" },
        { y: 420, label: "EMERGING", sub: "Career switchers, grads" },
      ].map((p) => (
        <g key={p.label}>
          <rect x="1120" y={p.y} width="220" height="32" stroke="#D2D5DA" fill="#fff" vectorEffect="non-scaling-stroke" />
          <text x="1132" y={p.y + 14} fontFamily="Orbitron, sans-serif" fontWeight="700" fontSize="11" fill="#0F1117">{p.label}</text>
          <text x="1132" y={p.y + 26} fontFamily="Outfit, sans-serif" fontSize="9" fill="#71747B">{p.sub}</text>
        </g>
      ))}

      {/* Facility → people */}
      <line x1="1060" y1="290" x2="1100" y2="290" stroke="url(#eco-line)" strokeWidth="1.5" strokeDasharray="4 3" />
      <text x="1080" y="282" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="9" fill="#71747B">RUN BY</text>
    </svg>
  );
}

function NodeIcon({ name, x, y, color = "#0F1117" }) {
  const t = `translate(${x},${y})`;
  const p = { fill: "none", stroke: color, strokeWidth: "1.3", strokeLinecap: "round", strokeLinejoin: "round" };
  const fillDot = { fill: color };
  const icons = {
    grid: (
      <>
        <line x1="0" y1="9" x2="18" y2="9" />
        <line x1="9" y1="0" x2="9" y2="18" />
        <circle cx="9" cy="9" r="2.5" />
      </>
    ),
    substation: (
      <>
        <rect x="3" y="7" width="12" height="9" />
        <line x1="9" y1="2" x2="3" y2="7" />
        <line x1="9" y1="2" x2="15" y2="7" />
        <line x1="9" y1="10" x2="9" y2="14" />
        <line x1="7" y1="12" x2="11" y2="12" />
      </>
    ),
    switchgear: (
      <>
        <rect x="2" y="2" width="14" height="14" />
        <line x1="2" y1="9" x2="16" y2="9" />
        <line x1="9" y1="2" x2="9" y2="16" />
      </>
    ),
    ups: (
      <>
        <rect x="2" y="6" width="14" height="9" />
        <rect x="6" y="3" width="6" height="3" />
        <line x1="6" y1="10.5" x2="9" y2="10.5" />
        <line x1="11" y1="8.5" x2="11" y2="12.5" />
        <line x1="9.5" y1="10.5" x2="12.5" y2="10.5" />
      </>
    ),
    generators: (
      <>
        <rect x="2" y="6" width="14" height="9" />
        <circle cx="6.5" cy="10.5" r="1.8" />
        <circle cx="12" cy="10.5" r="1.8" />
        <line x1="3" y1="6" x2="5" y2="3" />
        <line x1="15" y1="6" x2="13" y2="3" />
      </>
    ),
    cooling: (
      <>
        <line x1="9" y1="1" x2="9" y2="17" />
        <line x1="2" y1="9" x2="16" y2="9" />
        <line x1="3.5" y1="3.5" x2="14.5" y2="14.5" />
        <line x1="14.5" y1="3.5" x2="3.5" y2="14.5" />
      </>
    ),
    compute: (
      <>
        <rect x="2" y="2" width="14" height="3.5" />
        <rect x="2" y="7.25" width="14" height="3.5" />
        <rect x="2" y="12.5" width="14" height="3.5" />
        <circle cx="5" cy="3.75" r="0.6" {...fillDot} />
        <circle cx="5" cy="9" r="0.6" {...fillDot} />
        <circle cx="5" cy="14.25" r="0.6" {...fillDot} />
      </>
    ),
    pdu: (
      <>
        <rect x="2" y="6" width="14" height="6" />
        <circle cx="5" cy="9" r="0.9" {...fillDot} />
        <circle cx="9" cy="9" r="0.9" {...fillDot} />
        <circle cx="13" cy="9" r="0.9" {...fillDot} />
      </>
    ),
    network: (
      <>
        <rect x="2" y="9" width="14" height="5" />
        <circle cx="5" cy="11.5" r="0.7" {...fillDot} />
        <circle cx="9" cy="11.5" r="0.7" {...fillDot} />
        <circle cx="13" cy="11.5" r="0.7" {...fillDot} />
        <line x1="5" y1="9" x2="5" y2="4" />
        <line x1="13" y1="9" x2="13" y2="4" />
        <circle cx="5" cy="3" r="1" />
        <circle cx="13" cy="3" r="1" />
      </>
    ),
    ops: (
      <>
        <rect x="1.5" y="3" width="15" height="9" />
        <line x1="6" y1="15.5" x2="12" y2="15.5" />
        <line x1="9" y1="12" x2="9" y2="15.5" />
        <line x1="4" y1="6" x2="7" y2="6" />
      </>
    ),
    metroFiber: (
      <>
        <rect x="2" y="7" width="3.5" height="9" />
        <rect x="7.25" y="3" width="3.5" height="13" />
        <rect x="12.5" y="9" width="3.5" height="7" />
      </>
    ),
    longHaul: (
      <>
        <path d="M2 9 Q 9 3, 16 9" />
        <path d="M2 13 Q 9 7, 16 13" />
        <circle cx="2" cy="9" r="1" {...fillDot} />
        <circle cx="16" cy="9" r="1" {...fillDot} />
      </>
    ),
    subsea: (
      <>
        <path d="M1.5 6 Q 5 3, 9 6 T 16.5 6" />
        <path d="M1.5 9 Q 5 6, 9 9 T 16.5 9" />
        <line x1="9" y1="12" x2="9" y2="16" />
        <line x1="5" y1="16" x2="13" y2="16" />
      </>
    ),
    edge: (
      <>
        {[3, 9, 15].map((cx) =>
          [3, 9, 15].map((cy) => (
            <circle
              key={`${cx}-${cy}`}
              cx={cx}
              cy={cy}
              r="1.4"
              {...((cx + cy) % 6 === 0 ? fillDot : p)}
            />
          ))
        )}
      </>
    ),
    peering: (
      <>
        <circle cx="9" cy="9" r="2.2" />
        <circle cx="3" cy="3" r="1.5" />
        <circle cx="15" cy="3" r="1.5" />
        <circle cx="3" cy="15" r="1.5" />
        <circle cx="15" cy="15" r="1.5" />
        <line x1="4" y1="4" x2="7.5" y2="7.5" />
        <line x1="14" y1="4" x2="10.5" y2="7.5" />
        <line x1="4" y1="14" x2="7.5" y2="10.5" />
        <line x1="14" y1="14" x2="10.5" y2="10.5" />
      </>
    ),
    aiTrain: (
      <>
        <rect x="3" y="3" width="12" height="12" />
        <rect x="6" y="6" width="6" height="6" />
        <line x1="9" y1="0" x2="9" y2="3" />
        <line x1="9" y1="15" x2="9" y2="18" />
        <line x1="0" y1="9" x2="3" y2="9" />
        <line x1="15" y1="9" x2="18" y2="9" />
        <line x1="3" y1="3" x2="1" y2="1" />
        <line x1="15" y1="15" x2="17" y2="17" />
      </>
    ),
    aiInfer: (
      <>
        <polygon points="9,1 11,7 17,7 12,11 14,17 9,13 4,17 6,11 1,7 7,7" />
      </>
    ),
    cloud: (
      <>
        <path d="M5.5 13 Q 2 13, 2 10 Q 2 7, 5 7 Q 6 4, 9 4 Q 13 4, 13 8 Q 16.5 8, 16.5 10.5 Q 16.5 13, 13 13 Z" />
      </>
    ),
    cdn: (
      <>
        <circle cx="9" cy="9" r="7" />
        <line x1="2" y1="9" x2="16" y2="9" />
        <path d="M9 2 Q 13 9, 9 16" />
        <path d="M9 2 Q 5 9, 9 16" />
      </>
    ),
    enterprise: (
      <>
        <rect x="3" y="3" width="12" height="13" />
        <line x1="5.5" y1="6" x2="7" y2="6" />
        <line x1="9" y1="6" x2="10.5" y2="6" />
        <line x1="12" y1="6" x2="13.5" y2="6" />
        <line x1="5.5" y1="9" x2="7" y2="9" />
        <line x1="9" y1="9" x2="10.5" y2="9" />
        <line x1="12" y1="9" x2="13.5" y2="9" />
        <line x1="8" y1="12" x2="10" y2="12" />
        <line x1="8" y1="12" x2="8" y2="16" />
        <line x1="10" y1="12" x2="10" y2="16" />
      </>
    ),
  };
  const inner = icons[name];
  if (!inner) return null;
  return <g transform={t} {...p}>{inner}</g>;
}

function Node({ x, y, w, h, label, sub, icon }) {
  const nx = Number(x);
  const ny = Number(y);
  const nw = Number(w);
  const iconColor = "#0F1117";
  return (
    <g>
      <rect
        x={nx}
        y={ny}
        width={nw}
        height={Number(h)}
        fill="#fff"
        stroke="#0F1117"
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
      />
      {icon && (
        <NodeIcon name={icon} x={nx + nw - 26} y={ny + 8} color={iconColor} />
      )}
      <text
        x={nx + 10}
        y={ny + 22}
        fontFamily="Orbitron, sans-serif"
        fontWeight="800"
        fontSize="12"
        fill="#0F1117"
        letterSpacing="-0.01em"
      >
        {label}
      </text>
      <text
        x={nx + 10}
        y={ny + 38}
        fontFamily="Outfit, sans-serif"
        fontSize="10"
        fill="#5F6168"
      >
        {sub}
      </text>
    </g>
  );
}

export default function Ecosystem() {
  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      className="border-b hairline bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-12">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / The ecosystem
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance">
              The stack <span className="text-chrome">we build for.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-xl">
              Every cloud workload, every model, every stream traces back to the
              same four layers — power, facility, network, and the people who
              run them. This is the system DCYB staffs.
            </p>
          </div>
        </div>

        {/* Diagram panel */}
        <div className="chrome-surface p-4 md:p-6 relative">
          {/* Mobile swipe hint — sticky to visible area, not the scroll container */}
          <div
            aria-hidden
            className="lg:hidden absolute top-3 right-4 z-10 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/90 backdrop-blur border hairline font-mono-label text-[9px] tracking-[0.18em] text-[#52555B] pointer-events-none animate-pulse shadow-sm"
          >
            ← SWIPE →
          </div>
          <div className="overflow-x-auto">
            <EcosystemDiagram className="w-full h-auto min-w-[1100px]" />
          </div>
        </div>

        {/* Legend / key */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#D2D5DA] border hairline mt-6">
          {[
            { code: "01", label: "Power", body: "Grid, substations, backup, UPS." },
            { code: "02", label: "Facility", body: "Cooling, racks, PDU, ops floor." },
            { code: "03", label: "Network", body: "Fiber, subsea, edge, peering." },
            { code: "04", label: "Demand", body: "AI, cloud, CDN, enterprise." },
          ].map((l) => (
            <div key={l.code} className="bg-white p-5">
              <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
                Layer {l.code}
              </div>
              <div className="font-display text-lg font-bold text-[#0F1117] mb-1">
                {l.label}
              </div>
              <div className="text-[13px] text-[#52555B] leading-snug">
                {l.body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
